import i18n from '@/utils/i18n';
import { captureException } from '@/utils/sentry';
import { CountryCode } from 'libphonenumber-js';
import { EmployeeRole } from '@/types';
import { getE164PhoneNumber } from '@/utils/phone-number';
import { SalesPortalRestaurantEmployeesDocument } from '@sales-portal/domain/accounts/screens/employee-list/blocs/documents/sales-portal-restaurant-employees.generated';
import { useCallback } from 'react';
import { useExperienceToasts } from '@experiences/hooks/use-experience-toasts';
import { useSalesPortalInviteRestaurantEmployeeMutation } from '@/experiences/sales-portal/domain/accounts/screens/employee-list/containers/employee-invite-drawer/blocs/documents/sales-portal-invite-restaurant-employee.generated';

interface InviteEmployeeInput {
  communicationEmail: string;
  countryCode: CountryCode;
  isAdmin: boolean;
  loginEmail: string;
  name: string;
  phoneNumber: string;
  restaurantUUID: string;
  supplierUUID: string;
}

interface UseInviteRestaurantEmployeeMutatorResult {
  invite: (input: InviteEmployeeInput) => Promise<boolean>;
  inviting: boolean;
}

export const useInviteRestaurantEmployeeMutator =
  (): UseInviteRestaurantEmployeeMutatorResult => {
    const { showErrorToast, showToast } = useExperienceToasts();
    const [inviteRestaurantEmployee, { loading: inviting }] =
      useSalesPortalInviteRestaurantEmployeeMutation();

    const invite = useCallback(
      async (input: InviteEmployeeInput): Promise<boolean> => {
        const trimmedPhone = input.phoneNumber.trim();
        const phoneE164 = trimmedPhone
          ? getE164PhoneNumber(trimmedPhone, input.countryCode)
          : null;
        const loginEmail = input.loginEmail.trim() || null;
        const communicationEmail = input.communicationEmail.trim() || null;
        const name = input.name.trim();

        try {
          const { data } = await inviteRestaurantEmployee({
            refetchQueries: [
              {
                query: SalesPortalRestaurantEmployeesDocument,
                variables: {
                  restaurantUUID: input.restaurantUUID,
                  supplierUUID: input.supplierUUID,
                },
              },
            ],
            variables: {
              contactInfo: {
                email: communicationEmail,
                login_email: loginEmail,
                phone_number: phoneE164,
              },
              employeeRole: input.isAdmin
                ? EmployeeRole.Admin
                : EmployeeRole.User,
              name,
              restaurantUUID: input.restaurantUUID,
              supplierUUID: input.supplierUUID,
            },
          });
          if (data?.inviteRestaurantEmployee?.employee_id) {
            const toastKey = data.inviteRestaurantEmployee.is_duplicate_employee
              ? 'sales_portal.employee_invite.duplicate_toast'
              : 'sales_portal.employee_invite.success_toast';
            showToast({
              title: i18n.t(toastKey, { name }),
              variant: 'success',
            });
            return true;
          }
          captureException(
            new Error('inviteRestaurantEmployee returned no employee_id'),
            { extra: { response: data?.inviteRestaurantEmployee } },
          );
          showErrorToast(i18n.t('sales_portal.employee_invite.error_toast'));
          return false;
        } catch (error) {
          captureException(error);
          showErrorToast(i18n.t('sales_portal.employee_invite.error_toast'));
          return false;
        }
      },
      [inviteRestaurantEmployee, showErrorToast, showToast],
    );

    return { invite, inviting };
  };
